<template>
  <div
    class="w-[320px] max-h-[45vh] bg-white rounded-xl shadow-lg p-4 relative overflow-auto border"
  >
    <!-- Header -->
    <div
      class="flex justify-between items-center border-b border-gray-200 pb-2 mb-4"
    >
      <h2 class="text-sm font-medium">Notifications</h2>
      <button
        @click="$emit('close')"
        class="text-xs px-2 py-1 rounded-full border hover:text-white hover:bg-orange-500 transition-colors"
      >
        ✕
      </button>
    </div>

    <!-- Tabs -->
    <div class="flex space-x-2 mb-4">
      <!-- Notifications Button -->
      <button
        @click="activeTab = 'notifications'"
        :class="[
          'relative px-3 py-1 rounded-full text-sm duration-200',
          activeTab === 'notifications'
            ? 'bg-orange-500 text-white'
            : 'bg-gray-100 hover:bg-gray-200',
        ]"
      >
        Notifications
        <!-- Red blinking circle -->
        <span
          v-if="hasNotifications && activeTab !== 'notifications'"
          class="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full animate-ping"
        ></span>
      </button>

      <!-- Meetings Button -->
      <button
        @click="activeTab = 'meetings'"
        :class="[
          'relative px-3 py-1 rounded-full text-sm duration-200',
          activeTab === 'meetings'
            ? 'bg-orange-500 text-white'
            : 'bg-gray-100 hover:bg-gray-200',
        ]"
      >
        Meetings
        <!-- Red blinking circle -->
        <span
          v-if="hasMeetings && activeTab !== 'meetings'"
          class="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full animate-ping"
        ></span>
      </button>
    </div>

    <div class="w-full h-0.5 rounded-full bg-gray-100"></div>

    <!-- Notifications Tab -->
    <div v-if="activeTab === 'notifications'" class="mt-2">
      <div
        v-if="isHrStaffUser && pendingClientApprovalsList.length"
        class="mb-3 rounded-lg border border-amber-200 bg-amber-50/95 px-3 py-2.5 text-left"
      >
        <p class="text-xs font-semibold text-amber-950">Client approvals pending</p>
        <p class="mt-0.5 text-[11px] leading-snug text-amber-900/90">
          {{ pendingClientApprovalsList.length }} client record{{
            pendingClientApprovalsList.length === 1 ? "" : "s"
          }}
          from a VAM need review. Open
          <strong>Client List</strong>, use the Admin review column (filter: Pending review), then Approve or Reject.
        </p>
        <button
          type="button"
          class="mt-2 w-full rounded-lg bg-amber-600 px-2 py-1.5 text-center text-xs font-semibold text-white shadow-sm transition hover:bg-amber-700"
          @click="goToClientApprovals"
        >
          Open Client List
        </button>
      </div>
      <div v-if="activeNotifications.length" class="space-y-2">
        <div
          v-for="(note, index) in activeNotifications"
          :key="index"
          class="flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition"
          :class="noteBgClass(note)"
          @click="handleNotificationClick(note)"
        >
          <div class="flex-1 text-left">
            <div class="flex flex-start justify-between items-start w-full gap-2">
              <p class="text-sm min-w-0 flex-1">
                <span class="font-bold">
                  {{ note.first_name }} {{ note.last_name }}
                </span>
                <span class="font-normal text-gray-700">
                  {{ " " + note.text }}
                </span>
              </p>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  v-if="showMarkReadButton(note)"
                  type="button"
                  class="text-[10px] font-medium px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700 transition-colors"
                  aria-label="Mark as read"
                  @click.stop="markNotificationRead(note)"
                >
                  Mark read
                </button>
                <div
                  class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-100 transition-colors duration-200"
                >
                  <button
                    type="button"
                    aria-label="Dismiss notification"
                    @click.stop="clearNotification(note)"
                    class="text-red-500 hover:text-red-700"
                  >
                    <icon name="delete" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <p
              class="text-xs text-gray-500 flex items-center justify-between mt-2"
            >
              <span
                :class="noteBadgeClass(note)"
                class="flex items-center gap-1 rounded-full font-medium"
              >
                <!-- Approved -->
                <div
                  v-if="note.status === 'Approved'"
                  class="gap-1.5 items-center flex justify-center"
                >
                  <icon name="circle-check" class="w-4 text-green-600" />
                  <span class="text-green-600">Approved</span>
                </div>

                <!-- Pending -->
                <div
                  v-else-if="note.status === 'Pending'"
                  class="gap-1.5 items-center flex justify-center"
                >
                  <icon name="exclamation-circle" class="w-4 text-red-600" />
                  <span class="text-red-600">
                    {{ noteBadgeText(note) }}
                  </span>
                </div>
              </span>

              <span>{{ noteTime(note) }}</span>
            </p>
          </div>
        </div>
      </div>

      <p v-else class="italic text-center mt-4 text-sm text-gray-400">
        No notifications today
      </p>
    </div>

    <!-- Meetings Tab -->
    <div v-if="activeTab === 'meetings'" class="mt-2">
      <div v-if="meetings.length" class="space-y-3">
        <div
          v-for="(meeting, index) in meetings"
          :key="index"
          class="group relative flex justify-between items-start p-2 rounded-xl border bg-white shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
        >
          <!-- Meeting Details -->
          <div class="flex-1 px-1 space-y-1 text-left">
            <p
              class="text-sm font-semibold text-gray-800 group-hover:text-orange-600 transition"
            >
              {{ meeting.title }}
            </p>

            <p
              v-if="meeting.description"
              class="text-xs text-gray-400 leading-snug"
            >
              {{ meeting.description }}
            </p>

            <p class="text-xs text-gray-500 flex items-center gap-5">
              <icon name="time" class="w-1 text-gray-400" />
              {{ formatMeetingTime(meeting.date, meeting.start, meeting.end) }}
            </p>
            <div class="flex items-center justify-between mt-1">
              <a
                v-if="meeting.link"
                :href="meeting.link"
                target="_blank"
                class="text-xs font-medium text-blue-600 underline hover:text-blue-800"
              >
                Open meeting link
              </a>

              <!-- Status Badge -->
              <!-- <span
                v-if="user.role !== 'Admin'"
                class="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full"
              >
                Invited
              </span> -->
              <span
                class="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full"
              >
                Invited
              </span>
            </div>
          </div>
        </div>
      </div>

      <p v-else class="italic text-center mt-6 text-sm text-gray-400">
        No meetings today
      </p>
    </div>

    <!-- Clear All Button -->
    <div
      class="flex justify-center items-end mt-4"
      v-if="activeTab === 'notifications' && hasNotifications"
    >
      <button
        @click="clearAllNotifications"
        class="text-xs px-3 py-1 rounded-full border hover:text-white hover:bg-red-500 transition-colors"
      >
        Clear All
      </button>
    </div>
  </div>
</template>

<script>
import { useFetchDataStore } from "@/store/pinia_store";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import icon from "@/assets/icon.vue";
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export default {
  name: "NotificationPage",
  components: { icon },
  data() {
    return {
      activeTab: "notifications",
      notifications: [],
      meetings: [],
      user: null,
      hasNotifications: false,
      hasMeetings: false,
    };
  },
  computed: {
    store() {
      return useFetchDataStore();
    },
    isHrStaffUser() {
      const c = String(this.user?.role_code || "").toUpperCase();
      return c === "ADMIN" || c === "HR";
    },
    activeNotifications() {
      if (!this.user) return [];
      return this.notifications.filter(
        (n) => !n.clear_status || n.clear_status === "",
      );
    },
    pendingClientApprovalsList() {
      if (!this.isHrStaffUser) return [];
      const clients = this.store.client_details || [];
      return clients.filter((c) => {
        const s = String(c?.admin_approval_status ?? "Approved").trim();
        return s === "Pending";
      });
    },
  },
  methods: {
    goToClientApprovals() {
      this.$router.push("/client-module");
      this.closeModal();
    },
    closeModal() {
      this.$emit("close");
    },
    isNoteRead(note) {
      if (note.status === "Approved") return true;
      return note.admin_viewed_status === "Viewed";
    },
    showMarkReadButton(note) {
      if (!this.user || this.isNoteRead(note)) return false;
      return (
        this.isHrStaffUser &&
        note.status === "Pending" &&
        !!note.user_id
      );
    },
    async markNotificationRead(note) {
      if (!this.user) return;
      try {
        if (
          this.isHrStaffUser &&
          note.status === "Pending" &&
          note.admin_viewed_status !== "Viewed" &&
          note.user_id
        ) {
          await axios.patch(
            `${process.env.VUE_APP_API_BASE_URL}/user/update/${note.user_id}`,
            { admin_viewed_status: "Viewed" },
            { withCredentials: true },
          );
          note.admin_viewed_status = "Viewed";
          this.store.user_accounts = this.store.user_accounts.map((u) =>
            u.user_id === note.user_id
              ? { ...u, admin_viewed_status: "Viewed" }
              : u,
          );
          this.store.updateNotificationsCount(this.user);
        }
      } catch (err) {
        console.error(err);
      }
    },
    noteBgClass(note) {
      return this.isNoteRead(note)
        ? "bg-gray-50"
        : "bg-white hover:bg-gray-50";
    },
    noteBadgeClass(note) {
      if (note.status === "Pending" && !this.isNoteRead(note)) {
        return "px-2 py-1 rounded-full font-medium text-red-600 bg-red-100";
      } else if (note.status === "Pending" && this.isNoteRead(note)) {
        return "px-2 py-1 rounded-full font-medium text-green-400 bg-green-100";
      } else if (note.status === "Approved") {
        return "px-2 py-1 rounded-full font-medium text-green-600 bg-green-100";
      }
      return "";
    },
    noteBadgeText(note) {
      if (note.status === "Pending" && this.isNoteRead(note)) return "Read";
      if (note.status === "Pending" && !this.isNoteRead(note))
        return "Pending";
      return "";
    },

    noteTime(note) {
      const date = dayjs(
        this.isNoteRead(note) ? note.updated_at : note.created_at,
      );
      if (date.isToday()) return date.format("h:mm A");
      else if (date.isYesterday()) return "Yesterday " + date.format("h:mm A");
      else return date.format("MMM D, YYYY h:mm A");
    },
    formatMeetingTime(date, start, end) {
      const startTime = dayjs(`${date} ${start}`).format("h:mm A");
      const endTime = dayjs(`${date} ${end}`).format("h:mm A");
      return `${startTime} - ${endTime}`;
    },
    async loadUsers() {
      const rc = String(this.user?.role_code || "").toUpperCase();
      if (rc === "ADMIN" || rc === "HR") {
        await this.store.fetchClientDetails();
      }
      await this.store.fetchUserAccounts();
      const users = this.store.user_accounts || [];

      // Registration / approval alerts: HR & Admin only
      let accountNotes = [];
      if (this.user && this.isHrStaffUser) {
        const pending = users.filter(
          (u) =>
            u.approval === null ||
            u.approval === "Pending" ||
            u.approval === "",
        );
        const approved = users.filter((u) => u.approval === "Approved");

        accountNotes = [
          ...pending.map((u) => ({
            first_name: u.first_name,
            last_name: u.last_name,
            text: "is pending approval",
            created_at: u.created_at,
            updated_at: u.updated_at,
            user_id: u.user_id,
            admin_viewed_status: u.admin_viewed_status,
            status: "Pending",
            clear_status: u.clear_status || "",
          })),
          ...approved.map((u) => ({
            first_name: u.first_name,
            last_name: u.last_name,
            text: "has been approved",
            created_at: u.updated_at,
            updated_at: u.updated_at,
            user_id: u.user_id,
            admin_viewed_status: "Viewed",
            status: "Approved",
            clear_status: u.clear_status || "",
          })),
        ].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      }

      this.notifications = [...accountNotes].sort(
        (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
      );

      await this.$nextTick();
      this.hasNotifications =
        this.activeNotifications.length > 0 ||
        this.pendingClientApprovalsList.length > 0;
      this.store.updateNotificationsCount(this.user);
    },
    async loadSchedules() {
      await this.store.fetchSchedules();
      const list = this.store.schedules || [];
      const today = dayjs().format("YYYY-MM-DD");
      const now = dayjs(); // current time

      if (!this.user) return;

      // Helper function to check if schedule has not ended yet
      const isUpcoming = (s) => {
        const endTime = dayjs(`${s.date} ${s.end}`, "YYYY-MM-DD HH:mm");
        return endTime.isAfter(now); // only keep if end time is after now
      };

      if (this.isHrStaffUser) {
        // Admin sees only upcoming schedules today
        this.meetings = list.filter((s) => s.date === today && isUpcoming(s));
      } else {
        // Employee sees only upcoming schedules they are tagged in
        this.meetings = list.filter(
          (s) =>
            s.date === today &&
            s.tagEmployees.some((t) => t.employeeID === this.user.employeeID) &&
            isUpcoming(s),
        );
      }

      this.hasMeetings = this.meetings.length > 0;

      // Update top-bar notifications count reactively
      this.store.currentUser = this.user;
      this.store.updateNotificationsCount(this.user);
    },

    async clearNotification(note) {
      try {
        if (!this.user) return;

        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/user/update/${note.user_id}`,
          { clear_status: "Cleared" },
          { withCredentials: true },
        );

        note.clear_status = "Cleared";

        this.store.user_accounts = this.store.user_accounts.map((u) =>
          u.user_id === note.user_id ? { ...u, clear_status: "Cleared" } : u,
        );
        this.notifications = this.notifications.filter((n) => n !== note);

        // Recalculate top-bar count
        this.store.updateNotificationsCount(this.user);

        // Refresh modal state
        this.hasNotifications = this.activeNotifications.length > 0;
      } catch (err) {
        console.error(err);
      }
    },
    async clearAllNotifications() {
      try {
        const notificationsToClear = [...this.activeNotifications]; // clone to avoid mutation issues

        for (const note of notificationsToClear) {
          await this.clearNotification(note);
        }

        // Ensure modal state is updated
        this.hasNotifications = this.activeNotifications.length > 0;
      } catch (err) {
        console.error("Error clearing all notifications:", err);
      }
    },
    async handleNotificationClick(note) {
      try {
        if (!this.user) return;
        await this.markNotificationRead(note);

        if (this.isHrStaffUser) {
          this.$router.push("/user-account-module");
        }

        this.closeModal();
      } catch (err) {
        console.error(err);
      }
    },
    async fetchUser() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/auth/me`,
          {
            withCredentials: true,
          },
        );
        if (response.data) this.user = response.data;
        else window.location.href = "/";
      } catch {
        window.location.href = "/";
      }
    },
  },
  async mounted() {
    await this.fetchUser();
    await this.loadUsers();
    await this.loadSchedules();
  },
};
</script>
